import { areFieldsSet } from '@superbet-group/web.lib.utilities';
import { BonusType } from '@superbet-group/web.types';

import { logger } from '@app/logger';
import {
    Bonus,
    BonusKind,
} from '@core/models/bonus/Bonus';
import { BonusSubtype } from '@core/models/bonus/BonusSubtype';
import { MoneyUsage } from '@core/models/bonus/MoneyUsage';
import { transformBonusConditionsMap } from '@app/promotionHub/bonuses/mappers/transformBonusConditions';
import BonusDto from '@app/promotionHub/bonuses/types/betlerDto/BetlerBonusDto';
import BonusStatusDto from '@app/promotionHub/bonuses/types/betlerDto/BonusStatusDto';
import { BonusSubtypeDto } from '@app/promotionHub/bonuses/types/betlerDto/BonusSubtypeDto';
import ProductTypeDto from '@app/promotionHub/bonuses/types/betlerDto/ProductTypeDto';
import { WageringDto } from '@app/promotionHub/bonuses/types/betlerDto/WageringDto';
import BonusCategory from '@models/bonus/BonusCategory';
import { BonusStatus } from '@models/bonus/BonusStatus';
import { getBonusTermsAndConditionsUrl } from '@app/promotionHub/bonuses/utils/bonusHelpers';
import { isCallToActionTextValid } from '@src/app/promotionHub/shared/utils/helpers';
import { isValidUrl } from '@src/app/utils/domainHelpers';
import { PromoHubActionButton } from '@src/app/promotionHub/shared/types/PromoHubActionButton';

export function transformBetlerBonus(dto: BonusDto): Bonus {
    return {
        kind: BonusKind.Betler,
        id: dto.bonus_id,
        name: dto.name ?? dto.bonus_name,
        activationDate: new Date(dto.awarded),
        committed: dto.committed,
        expirationDate: new Date(dto.expires),
        completedDate: new Date(dto.empty_at || dto.expires),
        bonusAmount: dto.initial_amount,
        bonusAmountLeft: Math.max(dto.initial_amount - dto.total_spent, 0),
        bonusAwarded: dto.initial_amount,
        amountLeftToWager: getAmountLeftToWager(dto),
        wageredAmount: getBonusWageredAmount(dto),
        type: getBonusType(dto.product, dto.bonus_type, dto.redeemable),
        status: getBonusStatus(dto.state),
        category: getBonusCategory(dto.state),
        wageringRequirements: getWageringRequirements(dto),
        redeemedAmount: dto.total_won || 0,
        bonusSubtype: getBonusSubtype(dto.bonus_type, dto.redeemable),
        bonusPotentialWin: dto.uncommitted,
        pendingAmount: 0, // when betler adds pending amount update getPendingAmount()
        moneyUsage: getMoneyUsage(dto),
        bonusUsedAmount: dto.total_spent + dto.committed,
        bonusGainedAmount: Math.max(dto.uncommitted - dto.initial_amount, 0),
        priority: dto.priority ?? undefined,
        rewardAmount: getBonusReward(dto),
        rewardType: dto.reward_type,
        bonusActionButton: getBonusActionButton(dto),
        termsAndConditionsUrl: getBonusTermsAndConditionsUrl(dto.terms_and_conditions),
        offerDetails: dto.offer_details,
        ...areFieldsSet(dto, 'included') && { bonusConditions: transformBonusConditionsMap(dto.included) },
    };
}

function getBonusStatus(statusDto: BonusStatusDto): BonusStatus {
    const status = statusMappings[statusDto];
    if (!status) {
        logger.error(new Error(`Undefined status for bonus status: ${statusDto}`));
    }
    return status;
}

const statusMappings: Record<BonusStatusDto, BonusStatus> = {
    [BonusStatusDto.Expired]: BonusStatus.Expired,
    [BonusStatusDto.InProgress]: BonusStatus.Active,
    [BonusStatusDto.Running]: BonusStatus.Active,
    [BonusStatusDto.Complete]: BonusStatus.Redeemed,
    [BonusStatusDto.New]: BonusStatus.Active,
};

function getBonusCategory(statusDto: BonusStatusDto): BonusCategory {
    const category = categoryMappings[statusDto];
    if (!category) {
        logger.error(new Error(`Undefined category for bonus status: ${statusDto}`));
    }
    return category;
}

const categoryMappings: Record<BonusStatusDto, BonusCategory> = {
    [BonusStatusDto.Complete]: BonusCategory.History,
    [BonusStatusDto.Expired]: BonusCategory.History,
    [BonusStatusDto.InProgress]: BonusCategory.Active,
    [BonusStatusDto.Running]: BonusCategory.Active,
    [BonusStatusDto.New]: BonusCategory.Active,
};

function getAmountLeftToWager(dto: BonusDto) {
    return Math.max(isSportWageringType(dto)
        ? getWageringRequirements(dto) - getBonusWageredAmount(dto)
        : dto.initial_amount - dto.total_spent, 0);
}

function getWageringRequirements(dto: BonusDto) {
    return isSportWageringType(dto) && dto.wager_limit ? dto.wager_limit : dto.initial_amount;
}

function getBonusWageredAmount(dto: BonusDto) {
    return isSportWageringType(dto) ? (dto.wagered || 0) : (dto.total_spent + dto.committed);
}

function isSportWageringType(dto: BonusDto) {
    return getBonusSubtype(dto.bonus_type, dto.redeemable) === BonusSubtype.Wagering;
}

/**
 * Returns if bonus is wagering or not.
 * All bonus type except Wagering that have redeemable set to true are NonWagering.
 * Exception here is FreeBet bonus that has type BonusFunds and redeemable set to false, it is also NonWagering
 */
function getBonusSubtype(bonusSubtype: BonusSubtypeDto, redeemable = true): BonusSubtype {
    return bonusSubtype !== BonusSubtypeDto.Wagering && redeemable
        || bonusSubtype === BonusSubtypeDto.BonusFunds && !redeemable
        ? BonusSubtype.NonWagering
        : BonusSubtype.Wagering;
}

function getMoneyUsage(dto: BonusDto) {
    return dto.wagering === WageringDto.CashFirst ? MoneyUsage.CashFirst : MoneyUsage.BonusFirst;
}

function getBonusType(productType: ProductTypeDto, bonusType: BonusSubtypeDto, redeemable = true): BonusType {
    if (bonusType === BonusSubtypeDto.CashBonus) {
        return BonusType.Cash;
    } else if (bonusType === BonusSubtypeDto.HoneyToken) {
        return BonusType.HoneyToken;
    } else if (bonusType === BonusSubtypeDto.FreeSpins) {
        return BonusType.FreeSpins;
    } else if (productType === ProductTypeDto.Sportsbook) {
        return redeemable ? BonusType.Sport : BonusType.Freebet;
    } else if (productType === ProductTypeDto.Gaming) {
        return BonusType.Casino;
    } else {
        throw new Error(`Undefined type for bonus and product type combination: ${bonusType}, ${productType} `);
    }
}

/**
 * TODO: Betler is developing solution for showing pending amount on bonuses, until they do it we need to hide pending amount
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getPendingAmount(dto: BonusDto) {
    return isSportWageringType(dto) && !dto.all_bets_settled ? ((dto.total_spent || 0) - (dto.wagered || 0)) : 0;
}

function getBonusReward(dto: BonusDto) {
    if (dto.initial_amount > 0) {
        return dto.initial_amount;
    } else if (dto.max_reward_amount) {
        return dto.max_reward_amount;
    } else if (dto.count) {
        return dto.count;
    } else {
        return 0;
    }
}

function getBonusActionButton(dto: BonusDto): PromoHubActionButton {
    return {
        text: dto.call_to_action_text && isCallToActionTextValid(dto.call_to_action_text) ? dto.call_to_action_text : undefined,
        link: dto.call_to_action_deeplink && isValidUrl(dto.call_to_action_deeplink) ? dto.call_to_action_deeplink : undefined,
    };
}
